import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { 
  ArrowLeft, Save, Send, Camera, Trash2, CheckCircle, XCircle, 
  AlertTriangle, FileText, Building2, ChevronDown, ChevronRight, Upload, Image, Copy, Download, History, Languages, Loader2, FileDown,
  ClipboardList, Users, MessageSquare, FileWarning, Plus, UserCircle, WifiOff, CloudUpload, Calendar
} from "lucide-react";
import {
  initPhotoCache,
  cachePhotoForUpload,
  getPendingPhotoCount,
  syncPendingPhotos,
  fileToBase64,
  setupNetworkListeners
} from "../services/photoCache";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Conformity status options
const conformityOptions = [
  { value: "conformity", label: "Uygun / Conformity", color: "bg-green-100 text-green-800" },
  { value: "minor", label: "Minör Uygunsuzluk / Minor", color: "bg-yellow-100 text-yellow-800" },
  { value: "major", label: "Majör Uygunsuzluk / Major", color: "bg-red-100 text-red-800" },
  { value: "observation", label: "Gözlem / OFI", color: "bg-blue-100 text-blue-800" },
  { value: "na", label: "Uygulanamaz / N/A", color: "bg-gray-100 text-gray-800" }
];

const InteractiveReportForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  
  const [template, setTemplate] = useState(null);
  const [report, setReport] = useState(null);
  const [audits, setAudits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [approvedReports, setApprovedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyingCriteria, setCopyingCriteria] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [translatingItems, setTranslatingItems] = useState({});
  const [translatingAll, setTranslatingAll] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Check if this is a Stage 3 report (translation only for Stage 3)
  const isStage3 = template?.template_id?.includes("stage3") || false;
  
  // Form state
  const [selectedAudit, setSelectedAudit] = useState(searchParams.get("audit") || "");
  const [reportTitle, setReportTitle] = useState("");
  const [formData, setFormData] = useState({});
  const [criteriaData, setCriteriaData] = useState({});
  const [photos, setPhotos] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState("info");
  const [selectedSourceReport, setSelectedSourceReport] = useState("");
  
  // Interview records for Mülakat Formu
  const [interviewRecords, setInterviewRecords] = useState([]);
  const [newInterview, setNewInterview] = useState({ name: "", position: "", notes: "" });
  
  // Meeting participants for Toplantı Tutanağı
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState({ name: "", role: "", opening: true, closing: true });
  
  // Meeting details for FR.45
  const [meetingDetails, setMeetingDetails] = useState({
    openingDate: "",
    openingHour: "09:00",
    closingDate: "",
    closingHour: "17:00"
  });
  
  // Validation dialog state
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  
  // Offline photo cache state
  const [pendingPhotoCount, setPendingPhotoCount] = useState(0);
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine);
  const [syncingPhotos, setSyncingPhotos] = useState(false);

  // Initialize photo cache and network listeners
  useEffect(() => {
    initPhotoCache().then(() => {
      updatePendingCount();
    });
    
    const cleanup = setupNetworkListeners(
      async () => {
        setNetworkOnline(true);
        toast.success("İnternet bağlantısı sağlandı");
        // Auto-sync pending photos when coming online
        await autoSyncPhotos();
      },
      () => {
        setNetworkOnline(false);
        toast.warning("İnternet bağlantısı kesildi - Fotoğraflar önbelleğe alınacak");
      }
    );
    
    return cleanup;
  }, []);
  
  const updatePendingCount = async () => {
    const count = await getPendingPhotoCount();
    setPendingPhotoCount(count);
  };
  
  const autoSyncPhotos = async () => {
    if (!report?.report_id) return;
    
    const count = await getPendingPhotoCount();
    if (count === 0) return;
    
    setSyncingPhotos(true);
    toast.info(`${count} bekleyen fotoğraf yükleniyor...`);
    
    try {
      const result = await syncPendingPhotos(uploadPhotoToServer);
      if (result.synced > 0) {
        toast.success(`${result.synced} fotoğraf başarıyla yüklendi`);
        // Refresh photos display
        fetchReport();
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} fotoğraf yüklenemedi`);
      }
    } catch (error) {
      console.error('Auto sync error:', error);
    } finally {
      setSyncingPhotos(false);
      updatePendingCount();
    }
  };
  
  const uploadPhotoToServer = async (reportId, criterionId, files) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/interactive-reports/${reportId}/photos/${criterionId}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
  };

  // Get nonconformities from criteria data
  const getNonconformities = () => {
    const nonconformities = [];
    let ncNumber = 1;
    
    if (template?.sections) {
      template.sections.forEach(section => {
        section.criteria?.forEach(criterion => {
          // Check items within each criterion (a, b, c, etc.)
          criterion.items?.forEach(item => {
            const status = criteriaData[item.id]?.status;
            if (status === "minor" || status === "major") {
              nonconformities.push({
                nc_number: ncNumber++,
                criterion_id: item.id,
                criterion_code: criterion.code || criterion.id,
                // Turkish and English criterion text
                criterion_text_tr: item.text || "",
                criterion_text_en: item.text_en || item.text || "",
                section_name: section.name,
                section_name_en: section.name_en || section.name,
                classification: status,
                // Turkish and English details/evidence
                details_tr: criteriaData[item.id]?.evidence || criteriaData[item.id]?.description || "",
                details_en: criteriaData[item.id]?.evidence_en || ""
              });
            }
          });
        });
      });
    }
    
    return nonconformities;
  };

  const isNewReport = !id || id === "new";
  // Admin can edit any report, others can only edit draft/rejected
  const isAdmin = user?.role === "admin";
  const isEditable = !report || report.status === "draft" || report.status === "rejected" || isAdmin;
  const canEditCriteria = !isNewReport && isEditable; // Yeni raporda önce kaydet

  // Load template based on customer's audit phase and tenant
  const loadTemplate = useCallback(async (auditPhase = "2") => {
    try {
      // Select template based on tenant
      let templateId;
      if (tenantSlug === 'royalcert') {
        // RoyalCert uses different templates
        templateId = auditPhase === "3" ? "royalcert_stage3_audit" : "royalcert_stage2_audit";
      } else {
        // TRB uses standard templates
        templateId = auditPhase === "3" ? "stage3_audit_report" : "stage2_audit_report";
      }
      
      const response = await axios.get(`${API}/audit-templates/${templateId}`, { withCredentials: true });
      setTemplate(response.data);
      
      // Initialize expanded sections
      const sections = {};
      response.data.sections.forEach(s => { sections[s.id] = true; });
      setExpandedSections(sections);
    } catch (error) {
      toast.error("Şablon yüklenemedi");
    }
  }, [tenantSlug]);

  // Load existing report
  const loadReport = useCallback(async () => {
    if (isNewReport) return;
    try {
      const response = await axios.get(`${API}/interactive-reports/${id}`, { withCredentials: true });
      setReport(response.data);
      setReportTitle(response.data.title);
      setSelectedAudit(response.data.audit_id);
      setFormData(response.data.form_data || {});
      setCriteriaData(response.data.criteria_data || {});
      setPhotos(response.data.photos || {});
      setInterviewRecords(response.data.interview_records || []);
      setMeetingParticipants(response.data.meeting_participants || []);
      setMeetingDetails(response.data.meeting_details || {
        openingDate: "",
        openingHour: "09:00",
        closingDate: "",
        closingHour: "17:00"
      });
      
      // Load correct template based on report's template_id with tenant awareness
      const templateId = response.data.template_id || (tenantSlug === 'royalcert' ? "royalcert_stage2_audit" : "stage2_audit_report");
      const auditPhase = templateId.includes("stage3") ? "3" : "2";
      loadTemplate(auditPhase);
    } catch (error) {
      toast.error("Rapor bulunamadı");
      navigate("/reports");
    }
  }, [id, isNewReport, navigate, loadTemplate]);

  // Load audits
  const loadAudits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      setAudits(response.data);
    } catch (error) {
      console.error("Audits load error:", error);
    }
  }, []);

  // Load customers
  const loadCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      console.error("Customers load error:", error);
    }
  }, []);

  // Load approved reports for copying
  const loadApprovedReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/interactive-reports/approved/list`, { withCredentials: true });
      setApprovedReports(response.data);
    } catch (error) {
      console.error("Approved reports load error:", error);
    }
  }, []);

  useEffect(() => {
    // Wait for tenantSlug to be available before loading
    if (!tenantSlug) return;
    
    const init = async () => {
      setLoading(true);
      // Load default template (stage2), will be updated when audit is selected
      await Promise.all([loadTemplate("2"), loadAudits(), loadCustomers(), loadApprovedReports(), loadReport()]);
      setLoading(false);
    };
    init();
  }, [tenantSlug, loadTemplate, loadAudits, loadCustomers, loadApprovedReports, loadReport]);

  // Auto-fill facility info when audit is selected
  useEffect(() => {
    if (selectedAudit && isNewReport && customers.length > 0 && audits.length > 0) {
      const audit = audits.find(a => a.audit_id === selectedAudit);
      if (audit) {
        const customer = customers.find(c => c.customer_id === audit.customer_id);
        if (customer) {
          // Load correct template based on customer's audit phase
          const auditPhase = customer.audit_phase || "2";
          loadTemplate(auditPhase);
          
          // Auto-fill facility info from customer
          setFormData(prev => ({
            ...prev,
            facility_name: customer.facility_name || customer.name || "",
            business_name: customer.name || "",
            facility_address: customer.address || "",
            facility_id: customer.facility_id || "",
            facility_type: customer.facility_type || "",
            room_count: customer.room_count?.toString() || "",
            bed_count: customer.bed_count?.toString() || "",
            shift_count: customer.shift_count?.toString() || "",
            manager_count: customer.manager_count?.toString() || "",
            other_employee_count: customer.other_employee_count?.toString() || "",
            sustainable_manager: customer.sustainable_manager || "",
            other_standards: customer.other_standards || "",
            organization_info: customer.organization_info || ""
          }));
          toast.success(`${auditPhase}. Aşama şablonu ve tesis bilgileri yüklendi`);
        }
      }
    }
  }, [selectedAudit, isNewReport, customers, audits, loadTemplate]);

  // Copy criteria from approved report
  const handleCopyCriteria = async () => {
    if (!selectedSourceReport) {
      toast.error("Lütfen bir kaynak rapor seçin");
      return;
    }
    
    if (!report) {
      toast.error("Önce raporu kaydedin");
      return;
    }
    
    setCopyingCriteria(true);
    try {
      const response = await axios.post(
        `${API}/interactive-reports/${report.report_id}/copy-criteria/${selectedSourceReport}`,
        {},
        { withCredentials: true }
      );
      setCriteriaData(response.data.criteria_data || {});
      setSelectedSourceReport("");
      toast.success("Kriter değerlendirmeleri önceki rapordan kopyalandı");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kopyalama başarısız");
    } finally {
      setCopyingCriteria(false);
    }
  };

  // Update form field
  const updateFormField = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // Update criteria item
  const updateCriteriaItem = (itemId, field, value) => {
    setCriteriaData(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  // Translate single evidence field
  const handleTranslateEvidence = async (itemId) => {
    const evidence = criteriaData[itemId]?.evidence;
    if (!evidence || !evidence.trim()) {
      toast.error("Çevrilecek metin yok");
      return;
    }
    
    setTranslatingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await axios.post(`${API}/translate`, { text: evidence }, { withCredentials: true });
      setCriteriaData(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], evidence_en: response.data.translated }
      }));
      toast.success("Çeviri tamamlandı");
    } catch (error) {
      toast.error("Çeviri başarısız");
    } finally {
      setTranslatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Translate all evidence fields
  const handleTranslateAll = async () => {
    if (!report) {
      toast.error("Önce raporu kaydedin");
      return;
    }
    
    setTranslatingAll(true);
    try {
      const response = await axios.post(
        `${API}/interactive-reports/${report.report_id}/translate-evidence`,
        {},
        { withCredentials: true }
      );
      toast.success(`${response.data.translated_count} alan çevrildi`);
      await loadReport();
    } catch (error) {
      toast.error("Toplu çeviri başarısız");
    } finally {
      setTranslatingAll(false);
    }
  };

  // Handle photo upload with offline support
  const handlePhotoUpload = async (fieldId, files) => {
    if (!report) {
      toast.error("Önce raporu kaydedin");
      return;
    }
    
    // First try to upload directly - don't rely on isOnline() alone
    for (const file of files) {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      
      try {
        // Auto-save current changes before uploading photo
        await axios.put(`${API}/interactive-reports/${report.report_id}`, {
          title: reportTitle,
          form_data: formData,
          criteria_data: criteriaData,
          interview_records: interviewRecords,
          meeting_participants: meetingParticipants,
          meeting_details: meetingDetails
        }, { withCredentials: true });
        
        // Upload the photo
        await axios.post(
          `${API}/interactive-reports/${report.report_id}/photos/${fieldId}`,
          uploadFormData,
          { 
            withCredentials: true, 
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000 // 30 second timeout
          }
        );
        toast.success("Fotoğraf yüklendi");
        
        // Only refresh photos, not the entire report (to keep accordion open)
        try {
          const response = await axios.get(`${API}/interactive-reports/${report.report_id}`, { withCredentials: true });
          setPhotos(response.data.photos || {});
        } catch (e) {
          console.error("Failed to refresh photos:", e);
        }
      } catch (error) {
        console.error('Photo upload failed:', error);
        
        // Check if it's a network error or server error
        const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
        
        if (isNetworkError) {
          // Cache for later upload
          try {
            const base64Data = await fileToBase64(file);
            await cachePhotoForUpload(
              report.report_id,
              fieldId,
              base64Data,
              file.name,
              file.type
            );
            toast.warning(`"${file.name}" önbelleğe alındı - bağlantı gelince yüklenecek`);
            await updatePendingCount();
          } catch (cacheError) {
            console.error('Failed to cache photo:', cacheError);
            toast.error(`"${file.name}" yüklenemedi ve önbelleğe alınamadı`);
          }
        } else {
          // Server error - show the error message
          const errorMessage = error.response?.data?.detail || "Fotoğraf yüklenemedi";
          toast.error(errorMessage);
        }
      }
    }
  };

  // Delete photo
  const handleDeletePhoto = async (fieldId, photoIndex) => {
    if (!window.confirm("Bu fotoğrafı silmek istediğinizden emin misiniz?")) return;
    
    // Auto-save current changes before deleting photo
    try {
      await axios.put(`${API}/interactive-reports/${report.report_id}`, {
        title: reportTitle,
        form_data: formData,
        criteria_data: criteriaData,
        interview_records: interviewRecords,
        meeting_participants: meetingParticipants,
        meeting_details: meetingDetails
      }, { withCredentials: true });
    } catch (error) {
      toast.error("Değişiklikler kaydedilemedi");
      return;
    }
    
    try {
      await axios.delete(
        `${API}/interactive-reports/${report.report_id}/photos/${fieldId}/${photoIndex}`,
        { withCredentials: true }
      );
      toast.success("Fotoğraf silindi");
      
      // Only reload photos, keep current form state
      const response = await axios.get(`${API}/interactive-reports/${report.report_id}`, { withCredentials: true });
      setPhotos(response.data.photos || {});
      setReport(response.data);
    } catch (error) {
      toast.error("Fotoğraf silinemedi");
    }
  };

  // Save report
  const handleSave = async () => {
    if (!selectedAudit) {
      toast.error("Lütfen bir denetim seçin");
      return;
    }
    if (!reportTitle.trim()) {
      toast.error("Lütfen rapor başlığı girin");
      return;
    }

    setSaving(true);
    try {
      if (isNewReport) {
        // Use the template_id from loaded template
        const templateId = template?.template_id || "stage2_audit_report";
        const response = await axios.post(`${API}/interactive-reports`, {
          audit_id: selectedAudit,
          template_id: templateId,
          title: reportTitle,
          form_data: formData,
          criteria_data: criteriaData,
          interview_records: interviewRecords,
          meeting_participants: meetingParticipants,
          meeting_details: meetingDetails
        }, { withCredentials: true });
        
        toast.success("Rapor oluşturuldu");
        navigate(`/interactive-report/${response.data.report_id}`, { replace: true });
      } else {
        await axios.put(`${API}/interactive-reports/${id}`, {
          title: reportTitle,
          form_data: formData,
          criteria_data: criteriaData,
          interview_records: interviewRecords,
          meeting_participants: meetingParticipants,
          meeting_details: meetingDetails
        }, { withCredentials: true });
        
        toast.success("Rapor kaydedildi");
        await loadReport();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  // Submit for review
  // Validate report before submission
  const validateReportForSubmission = () => {
    const errors = [];
    
    // 1. Check interview records (görüşülen kişiler)
    if (!interviewRecords || interviewRecords.length === 0) {
      errors.push({
        type: "interview",
        message: "Görüşülen kişiler eklenmemiş",
        description: "Mülakat Formu sekmesinden en az bir görüşülen kişi ekleyin"
      });
    }
    
    // 2. Check meeting participants (katılımcılar)
    if (!meetingParticipants || meetingParticipants.length === 0) {
      errors.push({
        type: "participants",
        message: "Toplantı katılımcıları eklenmemiş",
        description: "Toplantı Tutanağı sekmesinden katılımcı ekleyin"
      });
    }
    
    // 3. Check all criteria items are evaluated
    const unevaluatedCriteria = [];
    if (template?.sections) {
      template.sections.forEach(section => {
        section.criteria?.forEach(criterion => {
          criterion.items?.forEach(item => {
            const itemData = criteriaData[item.id];
            if (!itemData || !itemData.status) {
              unevaluatedCriteria.push({
                code: criterion.code,
                itemId: item.id,
                text: item.text?.substring(0, 50) + "..."
              });
            }
          });
        });
      });
    }
    
    if (unevaluatedCriteria.length > 0) {
      errors.push({
        type: "criteria",
        message: `${unevaluatedCriteria.length} kriter maddesi değerlendirilmemiş`,
        description: "Tüm kriter maddelerini değerlendirin",
        details: unevaluatedCriteria.slice(0, 5).map(c => `${c.code} - ${c.text}`).join("\n") + 
                 (unevaluatedCriteria.length > 5 ? `\n... ve ${unevaluatedCriteria.length - 5} madde daha` : "")
      });
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    if (!report) return;
    
    // Validate before submission - BUT SKIP FOR ADMINS
    if (user?.role !== "admin") {
      const errors = validateReportForSubmission();
      if (errors.length > 0) {
        setValidationErrors(errors);
        setShowValidationDialog(true);
        return;
      }
    }
    
    if (!window.confirm("Raporu onaya göndermek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.patch(`${API}/interactive-reports/${report.report_id}/submit`, {}, { withCredentials: true });
      toast.success("Rapor onaya gönderildi");
      await loadReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gönderme başarısız");
    }
  };

  // Export to Word
  const handleExportWord = async () => {
    if (!report) return;
    
    try {
      const response = await axios.get(`${API}/export/interactive-report/${report.report_id}/word`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportTitle || 'rapor'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rapor Word olarak indirildi");
    } catch (error) {
      toast.error("İndirme başarısız");
    }
  };

  // Export to PDF - Currently shows instruction to use Word
  const handleExportPdf = async () => {
    if (!report) return;
    
    toast.info(
      "PDF dönüştürme şu an kullanılamıyor. Word dosyasını indirip, 'Farklı Kaydet > PDF' seçeneğini kullanabilirsiniz.", 
      { duration: 6000 }
    );
  };

  // Download meeting minutes
  const handleDownloadMeetingMinutes = async () => {
    if (!selectedAudit) {
      toast.error("Lütfen önce bir denetim seçin");
      return;
    }
    
    // First save the meeting details and participants
    if (report?.report_id) {
      try {
        await axios.put(`${API}/interactive-reports/${report.report_id}`, {
          title: reportTitle,
          form_data: formData,
          criteria_data: criteriaData,
          interview_records: interviewRecords,
          meeting_participants: meetingParticipants,
          meeting_details: meetingDetails
        }, { withCredentials: true });
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }
    
    try {
      // Include meeting details in the request
      const response = await axios.post(`${API}/royalcert/forms/FR.45/download-with-data/${selectedAudit}`, {
        meeting_participants: meetingParticipants,
        meeting_details: meetingDetails
      }, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const link = document.createElement('a');
      link.href = url;
      const auditInfo = audits.find(a => a.audit_id === selectedAudit);
      const customerName = auditInfo?.customer_name || 'Toplanti';
      link.setAttribute('download', `FR.45_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("FR.45 Toplantı Tutanağı indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İndirme başarısız");
    }
  };

  // Download Audit Plan
  const handleDownloadAuditPlan = async () => {
    if (!selectedAudit) {
      toast.error("Lütfen önce bir denetim seçin");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/audits/${selectedAudit}/audit-plan`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Denetim_Plani.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Denetim Planı indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İndirme başarısız");
    }
  };

  // Download Team Assignment Form
  const handleDownloadTeamAssignment = async () => {
    if (!selectedAudit) {
      toast.error("Lütfen önce bir denetim seçin");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/audits/${selectedAudit}/team-assignment`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Ekip_Atama_Formu.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Ekip Atama Formu indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İndirme başarısız");
    }
  };

  // Download Interview Form
  const handleDownloadInterviewForm = async () => {
    if (!selectedAudit) {
      toast.error("Lütfen önce bir denetim seçin");
      return;
    }
    
    try {
      // Include report_id if available to get interview records
      let url = `${API}/audits/${selectedAudit}/interview-form`;
      if (report?.report_id) {
        url += `?report_id=${report.report_id}`;
      }
      
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Mulakat_Formu.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Mülakat Formu indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İndirme başarısız");
    }
  };

  // Download Nonconformity Report for a specific nonconformity
  const handleDownloadNonconformityReport = async (ncData = null) => {
    if (!selectedAudit) {
      toast.error("Lütfen önce bir denetim seçin");
      return;
    }
    
    try {
      const requestData = ncData || { classification: "minor" };
      const response = await axios.post(`${API}/audits/${selectedAudit}/nonconformity-report`, 
        requestData,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
      const link = document.createElement('a');
      link.href = url;
      const fileName = ncData?.nc_number 
        ? `Uygunsuzluk_Raporu_${ncData.nc_number}.docx`
        : `Uygunsuzluk_Raporu.docx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Uygunsuzluk Raporu ${ncData?.nc_number ? `#${ncData.nc_number}` : ''} indirildi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İndirme başarısız");
    }
  };

  // Download all nonconformity reports as separate files
  const handleDownloadAllNonconformityReports = async () => {
    const nonconformities = getNonconformities();
    if (nonconformities.length === 0) {
      toast.error("Uygunsuzluk bulunamadı");
      return;
    }
    
    toast.info(`${nonconformities.length} adet uygunsuzluk raporu indiriliyor...`);
    
    for (const nc of nonconformities) {
      await handleDownloadNonconformityReport(nc);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // Delete report (admin only)
  const handleDeleteReport = async () => {
    if (!window.confirm("Bu raporu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/interactive-reports/${report.report_id}`, { withCredentials: true });
      toast.success("Rapor silindi");
      navigate("/reports");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme başarısız");
    }
  };

  // Withdraw report (pull back from review to draft)
  const handleWithdrawReport = async () => {
    if (!window.confirm("Raporu geri çekmek istediğinizden emin misiniz? Rapor taslak durumuna döner.")) {
      return;
    }
    
    try {
      await axios.patch(`${API}/interactive-reports/${report.report_id}/withdraw`, {}, { withCredentials: true });
      toast.success("Rapor geri çekildi, taslak olarak düzenleyebilirsiniz");
      // Reload report
      const response = await axios.get(`${API}/interactive-reports/${report.report_id}`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Geri çekme başarısız");
    }
  };

  // Revoke approval (admin only) - set report back to draft
  const handleRevokeApproval = async () => {
    const reason = window.prompt("Onay kaldırma gerekçesi:");
    if (reason === null) return; // User cancelled
    
    try {
      await axios.patch(`${API}/interactive-reports/${report.report_id}/review`, {
        status: "rejected",
        review_notes: reason || "Onay admin tarafından kaldırıldı"
      }, { withCredentials: true });
      toast.success("Rapor onayı kaldırıldı, düzenlemeye açıldı");
      // Reload report
      const response = await axios.get(`${API}/interactive-reports/${report.report_id}`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Onay kaldırma başarısız");
    }
  };

  // Toggle section
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Render form field
  const renderFormField = (field) => {
    const value = formData[field.id] || "";
    
    switch (field.type) {
      case "text":
      case "number":
      case "date":
        return (
          <Input
            type={field.type}
            value={value}
            onChange={(e) => updateFormField(field.id, e.target.value)}
            disabled={!isEditable}
            className="bg-white"
            data-testid={`field-${field.id}`}
          />
        );
      case "textarea":
        return (
          <Textarea
            value={value}
            onChange={(e) => updateFormField(field.id, e.target.value)}
            disabled={!isEditable}
            rows={3}
            className="bg-white"
            data-testid={`field-${field.id}`}
          />
        );
      case "select":
        return (
          <Select value={value} onValueChange={(v) => updateFormField(field.id, v)} disabled={!isEditable}>
            <SelectTrigger data-testid={`field-${field.id}`}>
              <SelectValue placeholder="Seçiniz..." />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((opt, i) => (
                <SelectItem key={i} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "multiselect":
        const selected = value ? value.split(",").filter(Boolean) : [];
        return (
          <div className="flex flex-wrap gap-2" data-testid={`field-${field.id}`}>
            {field.options.map((opt, i) => (
              <label key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={(e) => {
                    const newSelected = e.target.checked 
                      ? [...selected, opt] 
                      : selected.filter(s => s !== opt);
                    updateFormField(field.id, newSelected.join(","));
                  }}
                  disabled={!isEditable}
                  className="rounded"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      default:
        return <Input value={value} onChange={(e) => updateFormField(field.id, e.target.value)} disabled={!isEditable} />;
    }
  };

  // Render criteria item
  const renderCriteriaItem = (item, criterionId) => {
    const itemData = criteriaData[item.id] || {};
    const fieldPhotos = photos[item.id] || [];
    
    return (
      <div key={item.id} className="p-3 sm:p-4 bg-white border rounded-lg space-y-3" data-testid={`criteria-item-${item.id}`}>
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#E8F3EE] flex items-center justify-center">
            <span className="text-xs font-medium text-[#1A4D2E]">{item.id.replace(criterionId, "")}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#111827]">{item.text}</p>
            <p className="text-xs text-[#6B7280] mt-0.5 hidden sm:block">{item.text_en}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-3 sm:ml-11">
          <div>
            <Label className="text-xs text-[#6B7280]">Uygunluk Durumu</Label>
            <Select 
              value={itemData.status || ""} 
              onValueChange={(v) => updateCriteriaItem(item.id, "status", v)}
              disabled={!canEditCriteria}
            >
              <SelectTrigger className="mt-1" data-testid={`status-${item.id}`}>
                <SelectValue placeholder={isNewReport ? "Önce raporu kaydedin" : "Seçiniz..."} />
              </SelectTrigger>
              <SelectContent>
                {conformityOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className={`px-2 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-[#6B7280]">Açıklama / Kanıt</Label>
              {isStage3 && itemData.evidence && itemData.evidence.trim() && canEditCriteria && (
                <button
                  onClick={() => handleTranslateEvidence(item.id)}
                  disabled={translatingItems[item.id]}
                  className="flex items-center gap-1 text-xs text-[#1A4D2E] hover:text-[#1A4D2E]/80 disabled:opacity-50"
                  data-testid={`translate-${item.id}`}
                >
                  {translatingItems[item.id] ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Languages className="w-3 h-3" />
                  )}
                  <span>Çevir</span>
                </button>
              )}
            </div>
            <Textarea
              value={itemData.evidence || ""}
              onChange={(e) => updateCriteriaItem(item.id, "evidence", e.target.value)}
              placeholder={isNewReport ? "Önce raporu kaydedin..." : "Objektif delil, gözlem notları..."}
              rows={2}
              className="mt-1 text-sm"
              disabled={!canEditCriteria}
              data-testid={`evidence-${item.id}`}
            />
            {/* English translation */}
            {itemData.evidence_en && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                <p className="text-xs text-blue-600 font-medium mb-1">🇬🇧 English:</p>
                <p className="text-[#4B5563] italic">{itemData.evidence_en}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Photo upload section - only show after report is saved */}
        {!isNewReport && (
        <div className="sm:ml-11">
          <Label className="text-xs text-[#6B7280] flex items-center gap-1">
            <Camera className="w-3 h-3" /> Fotoğraflar
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {fieldPhotos.map((photo, idx) => (
              <div key={idx} className="relative group">
                <img 
                  src={`data:${photo.content_type};base64,${photo.data}`}
                  alt={photo.file_name}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                  onDoubleClick={() => setSelectedPhoto({ src: `data:${photo.content_type};base64,${photo.data}`, name: photo.file_name })}
                  title="Büyütmek için çift tıklayın"
                />
                {isEditable && (
                  <button
                    onClick={() => handleDeletePhoto(item.id, idx)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {canEditCriteria && (
              <label className="w-16 h-16 sm:w-20 sm:h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors">
                <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-[#9CA3AF]" />
                <span className="text-xs text-[#9CA3AF] mt-1">Ekle</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(item.id, e.target.files)}
                  data-testid={`photo-upload-${item.id}`}
                />
              </label>
            )}
          </div>
        </div>
        )}
      </div>
    );
  };

  // Render criteria section
  const renderCriteriaSection = (criterion) => {
    return (
      <div key={criterion.id} className="border rounded-xl overflow-hidden bg-[#F9FAFB]">
        <button
          onClick={() => toggleSection(criterion.id)}
          className="w-full px-3 sm:px-4 py-3 flex items-center justify-between text-left hover:bg-[#E8F3EE]/50 active:bg-[#E8F3EE] transition-colors"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <span className="px-2 py-1 bg-[#1A4D2E] text-white text-xs font-bold rounded flex-shrink-0">{criterion.code}</span>
            <div className="min-w-0">
              <h4 className="font-medium text-[#111827] text-sm sm:text-base truncate">{criterion.title}</h4>
              <p className="text-xs text-[#6B7280] hidden sm:block">{criterion.title_en}</p>
            </div>
          </div>
          {expandedSections[criterion.id] ? <ChevronDown className="w-5 h-5 text-[#6B7280] flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-[#6B7280] flex-shrink-0" />}
        </button>
        
        {expandedSections[criterion.id] && (
          <div className="px-3 sm:px-4 pb-4 space-y-3">
            <p className="text-sm text-[#4B5563] bg-white p-3 rounded-lg border-l-4 border-[#1A4D2E]">
              {criterion.description}
            </p>
            {criterion.items.map(item => renderCriteriaItem(item, criterion.id))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-[#4B5563]">Şablon yüklenemedi</p>
      </div>
    );
  }

  const statusLabels = {
    draft: { label: "Taslak", color: "bg-gray-100 text-gray-800" },
    submitted: { label: "Gönderildi", color: "bg-blue-100 text-blue-800" },
    under_review: { label: "İnceleniyor", color: "bg-yellow-100 text-yellow-800" },
    approved: { label: "Onaylandı", color: "bg-green-100 text-green-800" },
    rejected: { label: "Reddedildi", color: "bg-red-100 text-red-800" }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-20" data-testid="interactive-report-form">
      {/* Loading State */}
      {(loading || !template) && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] mx-auto mb-4" />
            <p className="text-[#4B5563]">Şablon yükleniyor...</p>
          </div>
        </div>
      )}

      {/* Main Content - Only render when template is loaded */}
      {!loading && template && (
        <>
      {/* Offline Status Banner */}
      {(!networkOnline || pendingPhotoCount > 0) && (
        <div className={`rounded-lg p-3 flex items-center justify-between ${!networkOnline ? 'bg-orange-100 border border-orange-300' : 'bg-blue-100 border border-blue-300'}`}>
          <div className="flex items-center gap-2">
            {!networkOnline ? (
              <>
                <WifiOff className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  Çevrimdışı mod - Fotoğraflar önbelleğe alınacak
                </span>
              </>
            ) : (
              <>
                <CloudUpload className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {pendingPhotoCount} fotoğraf yükleme bekliyor
                </span>
              </>
            )}
          </div>
          {networkOnline && pendingPhotoCount > 0 && (
            <Button 
              size="sm"
              onClick={autoSyncPhotos}
              disabled={syncingPhotos}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {syncingPhotos ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  <CloudUpload className="w-4 h-4 mr-1" />
                  Şimdi Yükle
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/reports")} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate">
              {isNewReport ? "Yeni 2. Aşama Raporu" : reportTitle}
            </h1>
            <p className="text-[#4B5563] text-sm truncate">{template?.name || 'Rapor Şablonu'}</p>
          </div>
          {report && (
            <span className={`px-3 py-1 text-xs sm:text-sm rounded-full whitespace-nowrap ${statusLabels[report.status]?.color}`}>
              {statusLabels[report.status]?.label}
            </span>
          )}
        </div>
        {isEditable && (
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              onClick={handleSave} 
              disabled={saving}
              className="gap-2 flex-1 sm:flex-none"
              data-testid="save-report-btn"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">{saving ? "Kaydediliyor..." : "Kaydet"}</span>
              <span className="sm:hidden">{saving ? "..." : "Kaydet"}</span>
            </Button>
            {report && (
              <Button 
                variant="outline"
                onClick={handleExportWord}
                className="gap-2 flex-1 sm:flex-none"
                data-testid="export-word-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Word İndir</span>
                <span className="sm:hidden">Word</span>
              </Button>
            )}
            {selectedAudit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline"
                    className="gap-2 flex-1 sm:flex-none"
                    data-testid="documents-dropdown-btn"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Belgeler</span>
                    <span className="sm:hidden">Belge</span>
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Denetim Belgeleri</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDownloadMeetingMinutes} data-testid="download-meeting-minutes">
                    <FileText className="w-4 h-4 mr-2" />
                    Toplantı Tutanağı
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadAuditPlan} data-testid="download-audit-plan">
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Denetim Planı
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadTeamAssignment} data-testid="download-team-assignment">
                    <Users className="w-4 h-4 mr-2" />
                    Ekip Atama Formu
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadInterviewForm} data-testid="download-interview-form">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Mülakat Formu
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDownloadNonconformityReport} data-testid="download-nonconformity">
                    <FileWarning className="w-4 h-4 mr-2" />
                    Uygunsuzluk Raporu
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {report && isStage3 && (
              <Button 
                variant="outline"
                onClick={handleTranslateAll}
                disabled={translatingAll}
                className="gap-2 flex-1 sm:flex-none"
                data-testid="translate-all-btn"
              >
                {translatingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Languages className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{translatingAll ? "Çevriliyor..." : "Tümünü Çevir"}</span>
                <span className="sm:hidden">{translatingAll ? "..." : "Çevir"}</span>
              </Button>
            )}
            {report && (report.status !== "draft" || report.review_history?.length > 0) && (
              <Button 
                variant="outline"
                onClick={() => setHistoryDialogOpen(true)}
                className="gap-2"
                data-testid="history-btn"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Geçmiş</span>
              </Button>
            )}
            {report && (user?.role === "admin" || user?.role === "super_admin") && (
              <Button 
                variant="outline"
                onClick={handleDeleteReport}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="delete-report-btn"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Sil</span>
              </Button>
            )}
            {report && (report.status === "submitted" || report.status === "under_review") && (user?.role === "admin" || user?.role === "super_admin" || report.created_by === user?.user_id) && (
              <Button 
                variant="outline"
                onClick={handleWithdrawReport}
                className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                data-testid="withdraw-report-btn"
              >
                <XCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Geri Çek</span>
              </Button>
            )}
            {report && report.status === "approved" && (user?.role === "admin" || user?.role === "super_admin") && (
              <Button 
                variant="outline"
                onClick={handleRevokeApproval}
                className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                data-testid="revoke-approval-btn"
              >
                <XCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Onayı Kaldır</span>
              </Button>
            )}
            {report && (report.status === "draft" || report.status === "rejected") && (
              <Button 
                onClick={handleSubmit}
                className="gap-2 flex-1 sm:flex-none bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                data-testid="submit-report-btn"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">{report.status === "rejected" ? "Tekrar Onaya Gönder" : "Onaya Gönder"}</span>
                <span className="sm:hidden">Gönder</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Rejection Notice Banner */}
      {report?.status === "rejected" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="rejection-banner">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-800">Rapor Reddedildi</h3>
              <p className="text-sm text-red-700 mt-1">
                <span className="font-medium">Gerekçe:</span> {report.review_notes || "Gerekçe belirtilmemiş"}
              </p>
              {report.reviewed_by_name && (
                <p className="text-xs text-red-600 mt-2">
                  Reddeden: {report.reviewed_by_name} • {report.reviewed_at ? new Date(report.reviewed_at).toLocaleDateString('tr-TR') : ''}
                </p>
              )}
              <p className="text-sm text-red-700 mt-3">
                Lütfen gerekli düzeltmeleri yapıp raporu tekrar onaya gönderin.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(value) => {
        // Prevent switching to criteria tabs if report is not saved yet
        if (isNewReport && (value === "section_A" || value === "section_B" || value === "section_C" || value === "section_D")) {
          toast.error("Kriter değerlendirmelerine geçmek için önce raporu kaydedin");
          return;
        }
        setActiveTab(value);
      }}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-[#F9FAFB] p-1 inline-flex min-w-max sm:w-full">
            <TabsTrigger value="info" className="data-[state=active]:bg-white gap-2 whitespace-nowrap">
              <Building2 className="w-4 h-4 hidden sm:block" />
              Tesis Bilgileri
            </TabsTrigger>
            <TabsTrigger value="section_A" className="data-[state=active]:bg-white whitespace-nowrap">
              A - Yönetim
            </TabsTrigger>
            <TabsTrigger value="section_B" className="data-[state=active]:bg-white whitespace-nowrap">
              B - Sosyal
            </TabsTrigger>
            <TabsTrigger value="section_C" className="data-[state=active]:bg-white whitespace-nowrap">
              C - Kültür
            </TabsTrigger>
            <TabsTrigger value="section_D" className="data-[state=active]:bg-white whitespace-nowrap">
              D - Çevre
            </TabsTrigger>
            <TabsTrigger value="interviews" className="data-[state=active]:bg-white gap-2 whitespace-nowrap">
              <UserCircle className="w-4 h-4 hidden sm:block" />
              Mülakatlar
            </TabsTrigger>
            <TabsTrigger value="participants" className="data-[state=active]:bg-white gap-2 whitespace-nowrap">
              <Users className="w-4 h-4 hidden sm:block" />
              Toplantı Katılımcıları
            </TabsTrigger>
            <TabsTrigger value="nonconformities" className="data-[state=active]:bg-white gap-2 whitespace-nowrap">
              <AlertTriangle className="w-4 h-4 hidden sm:block" />
              Uygunsuzluklar
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Info Tab - Basic form fields */}
        <TabsContent value="info" className="space-y-6">
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg">Denetim ve Rapor Bilgileri</CardTitle>
              <CardDescription>Denetim seçin ve rapor başlığını girin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Denetim *</Label>
                  <Select value={selectedAudit} onValueChange={setSelectedAudit} disabled={!isNewReport}>
                    <SelectTrigger className="mt-1" data-testid="select-audit">
                      <SelectValue placeholder="Denetim seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {audits.length === 0 ? (
                        <SelectItem value="no-audit" disabled>
                          {user?.role === "auditor" ? "Size atanmış denetim bulunmuyor" : "Denetim bulunmuyor"}
                        </SelectItem>
                      ) : (
                        audits.map((audit) => (
                          <SelectItem key={audit.audit_id} value={audit.audit_id}>
                            {audit.title} - {audit.customer_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {user?.role === "auditor" && (
                    <p className="text-xs text-[#9CA3AF] mt-1">
                      Sadece size atanan denetimleri görebilirsiniz
                    </p>
                  )}
                </div>
                <div>
                  <Label>Rapor Başlığı *</Label>
                  <Input
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    placeholder="Örn: 2. Aşama Denetim Raporu - Otel XYZ"
                    className="mt-1"
                    disabled={!isEditable}
                    data-testid="report-title-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Copy Criteria from Previous Report */}
          {report && isEditable && approvedReports.length > 0 && (
            <Card className="border-[#E8F3EE] bg-gradient-to-r from-[#1A4D2E]/5 to-[#1A4D2E]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Copy className="w-5 h-5 text-[#1A4D2E]" />
                  Önceki Rapordan Kriter Kopyala
                </CardTitle>
                <CardDescription>
                  Onaylanmış bir rapordan kriter değerlendirmelerini (A-B-C-D bölümleri) bu rapora kopyalayın. Tesis bilgileri kopyalanmaz.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Select value={selectedSourceReport} onValueChange={setSelectedSourceReport}>
                      <SelectTrigger data-testid="select-source-report">
                        <SelectValue placeholder="Kaynak rapor seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        {approvedReports.map((r) => (
                          <SelectItem key={r.report_id} value={r.report_id}>
                            {r.title} - {r.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleCopyCriteria}
                    disabled={!selectedSourceReport || copyingCriteria}
                    variant="outline"
                    className="gap-2"
                    data-testid="copy-criteria-btn"
                  >
                    <Copy className="w-4 h-4" />
                    {copyingCriteria ? "Kopyalanıyor..." : "Kriterleri Kopyala"}
                  </Button>
                </div>
                <p className="text-xs text-[#6B7280] mt-2">
                  Not: Fotoğraflar kopyalanmaz, her denetim için yeni fotoğraf yüklemeniz gerekir.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Section 1 - Facility Info */}
          {template?.sections?.filter(s => s.id === "section_1" || s.id === "section_2").map(section => (
            <Card key={section.id} className="border-[#E8F3EE]">
              <CardHeader className="cursor-pointer" onClick={() => toggleSection(section.id)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{section.title}</CardTitle>
                    <CardDescription>{section.title_en}</CardDescription>
                  </div>
                  {expandedSections[section.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </div>
              </CardHeader>
              {expandedSections[section.id] && (
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {section.fields.map(field => (
                      <div key={field.id} className={field.type === "textarea" || field.type === "multiselect" ? "md:col-span-2 lg:col-span-3" : ""}>
                        <Label className="text-sm">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        <p className="text-xs text-[#9CA3AF] mb-1">{field.label_en}</p>
                        {renderFormField(field)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* Criteria Sections */}
        {template?.sections?.filter(s => s.criteria).map(section => (
          <TabsContent key={section.id} value={section.id} className="space-y-4">
            <Card className="border-[#E8F3EE] bg-[#1A4D2E]/5">
              <CardHeader>
                <CardTitle className="text-xl text-[#1A4D2E]">{section.title}</CardTitle>
                <CardDescription>{section.title_en}</CardDescription>
              </CardHeader>
            </Card>
            
            {section.criteria.map(criterion => renderCriteriaSection(criterion))}
          </TabsContent>
        ))}

        {/* Interviews Tab - Mülakat Kayıtları */}
        <TabsContent value="interviews" className="space-y-6">
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-[#1A4D2E]" />
                Mülakat Kayıtları
              </CardTitle>
              <CardDescription>Saha denetimi sırasında yapılan mülakatları kaydedin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add new interview form */}
              {isEditable && (
                <div className="p-4 border border-dashed border-[#1A4D2E]/30 rounded-lg bg-[#F9FAFB]">
                  <h4 className="font-medium text-sm text-[#1A4D2E] mb-3">Yeni Mülakat Ekle</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="interview-name">Kişi Adı Soyadı</Label>
                      <Input
                        id="interview-name"
                        value={newInterview.name}
                        onChange={(e) => setNewInterview({...newInterview, name: e.target.value})}
                        placeholder="Örn: Ahmet Yılmaz"
                      />
                    </div>
                    <div>
                      <Label htmlFor="interview-position">Görevi</Label>
                      <Input
                        id="interview-position"
                        value={newInterview.position}
                        onChange={(e) => setNewInterview({...newInterview, position: e.target.value})}
                        placeholder="Örn: Sürdürülebilirlik Yöneticisi"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Label htmlFor="interview-notes">Notlar (Opsiyonel)</Label>
                      <Textarea
                        id="interview-notes"
                        value={newInterview.notes}
                        onChange={(e) => setNewInterview({...newInterview, notes: e.target.value})}
                        placeholder="Mülakat sırasında alınan notlar..."
                        rows={2}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Button
                        type="button"
                        onClick={() => {
                          if (!newInterview.name.trim() || !newInterview.position.trim()) {
                            toast.error("Kişi adı ve görevi zorunludur");
                            return;
                          }
                          setInterviewRecords([...interviewRecords, { ...newInterview, id: Date.now() }]);
                          setNewInterview({ name: "", position: "", notes: "" });
                          toast.success("Mülakat kaydı eklendi");
                        }}
                        className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Mülakat Ekle
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Interview list */}
              {interviewRecords.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-700">Kayıtlı Mülakatlar ({interviewRecords.length})</h4>
                  {interviewRecords.map((interview, index) => (
                    <div key={interview.id || index} className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center text-xs font-medium text-[#1A4D2E]">
                            {index + 1}
                          </span>
                          <span className="font-medium text-gray-900">{interview.name}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 ml-8">{interview.position}</p>
                        {interview.notes && (
                          <p className="text-sm text-gray-500 mt-2 ml-8 italic">"{interview.notes}"</p>
                        )}
                      </div>
                      {isEditable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setInterviewRecords(interviewRecords.filter((_, i) => i !== index));
                            toast.success("Mülakat kaydı silindi");
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Henüz mülakat kaydı eklenmemiş</p>
                  <p className="text-sm">Saha denetimi sırasında görüştüğünüz kişileri ekleyin</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meeting Participants Tab - Toplantı Katılımcıları */}
        <TabsContent value="participants" className="space-y-6">
          {/* Meeting Details Card */}
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1A4D2E]" />
                Toplantı Bilgileri (FR.45)
              </CardTitle>
              <CardDescription>Açılış ve kapanış toplantısı tarih/saat bilgilerini girin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opening Meeting */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">Açılış Toplantısı</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="opening-date" className="text-sm">Tarih</Label>
                      <Input
                        id="opening-date"
                        type="date"
                        value={meetingDetails.openingDate || ""}
                        onChange={(e) => setMeetingDetails({...meetingDetails, openingDate: e.target.value})}
                        disabled={!isEditable}
                        data-testid="opening-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="opening-hour" className="text-sm">Saat</Label>
                      <Input
                        id="opening-hour"
                        type="time"
                        value={meetingDetails.openingHour || "09:00"}
                        onChange={(e) => setMeetingDetails({...meetingDetails, openingHour: e.target.value})}
                        disabled={!isEditable}
                        data-testid="opening-hour-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Closing Meeting */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">Kapanış Toplantısı</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="closing-date" className="text-sm">Tarih</Label>
                      <Input
                        id="closing-date"
                        type="date"
                        value={meetingDetails.closingDate || ""}
                        onChange={(e) => setMeetingDetails({...meetingDetails, closingDate: e.target.value})}
                        disabled={!isEditable}
                        data-testid="closing-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="closing-hour" className="text-sm">Saat</Label>
                      <Input
                        id="closing-hour"
                        type="time"
                        value={meetingDetails.closingHour || "17:00"}
                        onChange={(e) => setMeetingDetails({...meetingDetails, closingHour: e.target.value})}
                        disabled={!isEditable}
                        data-testid="closing-hour-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Participants Card */}
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1A4D2E]" />
                Toplantı Katılımcıları
              </CardTitle>
              <CardDescription>Açılış ve kapanış toplantılarına katılan kişileri kaydedin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add new participant form */}
              {isEditable && (
                <div className="p-4 border border-dashed border-[#1A4D2E]/30 rounded-lg bg-[#F9FAFB]">
                  <h4 className="font-medium text-sm text-[#1A4D2E] mb-3">Yeni Katılımcı Ekle</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="participant-name">Adı Soyadı</Label>
                      <Input
                        id="participant-name"
                        value={newParticipant.name}
                        onChange={(e) => setNewParticipant({...newParticipant, name: e.target.value})}
                        placeholder="Örn: Ahmet Yılmaz"
                        data-testid="participant-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="participant-role">Görevi</Label>
                      <Input
                        id="participant-role"
                        value={newParticipant.role}
                        onChange={(e) => setNewParticipant({...newParticipant, role: e.target.value})}
                        placeholder="Örn: Genel Müdür"
                        data-testid="participant-role-input"
                      />
                    </div>
                    <div className="flex items-center gap-4 pt-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newParticipant.opening}
                          onChange={(e) => setNewParticipant({...newParticipant, opening: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-green-700">Açılış</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newParticipant.closing}
                          onChange={(e) => setNewParticipant({...newParticipant, closing: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-blue-700">Kapanış</span>
                      </label>
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        onClick={() => {
                          if (!newParticipant.name.trim() || !newParticipant.role.trim()) {
                            toast.error("Katılımcı adı ve görevi zorunludur");
                            return;
                          }
                          setMeetingParticipants([...meetingParticipants, { ...newParticipant, id: Date.now() }]);
                          setNewParticipant({ name: "", role: "", opening: true, closing: true });
                          toast.success("Katılımcı eklendi");
                        }}
                        className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                        data-testid="add-participant-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ekle
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Participants list */}
              {meetingParticipants.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-700">Kayıtlı Katılımcılar ({meetingParticipants.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#F9FAFB]">
                        <tr>
                          <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">#</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Adı Soyadı</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Görevi</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-green-700">Açılış</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-blue-700">Kapanış</th>
                          {isEditable && <th className="w-16"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {meetingParticipants.map((participant, index) => (
                          <tr key={participant.id || index} className="border-t">
                            <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{participant.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{participant.role}</td>
                            <td className="px-4 py-3 text-center">
                              {participant.opening !== false ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 rounded-full">✓</span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-400 rounded-full">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {participant.closing !== false ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full">✓</span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-400 rounded-full">-</span>
                              )}
                            </td>
                            {isEditable && (
                              <td className="px-4 py-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setMeetingParticipants(meetingParticipants.filter((_, i) => i !== index));
                                    toast.success("Katılımcı silindi");
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-participant-${index}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Henüz katılımcı eklenmemiş</p>
                  <p className="text-sm">Açılış ve kapanış toplantılarına katılan kişileri ekleyin</p>
                </div>
              )}

              {/* Download FR.45 Button */}
              {meetingParticipants.length > 0 && (
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleDownloadMeetingMinutes}
                    className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                    data-testid="download-fr45-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    FR.45 Toplantı Tutanağı İndir
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nonconformities Tab - Uygunsuzluklar */}
        <TabsContent value="nonconformities" className="space-y-6">
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Uygunsuzluklar
              </CardTitle>
              <CardDescription>
                Kriterlerde tespit edilen uygunsuzlukların listesi. Her uygunsuzluk için ayrı rapor oluşturulabilir.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {(() => {
                const nonconformities = getNonconformities();
                
                if (nonconformities.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                      <p className="font-medium">Uygunsuzluk Bulunamadı</p>
                      <p className="text-sm">Tüm kriterler uygun durumda veya henüz değerlendirilmemiş.</p>
                    </div>
                  );
                }
                
                const majorCount = nonconformities.filter(nc => nc.classification === "major").length;
                const minorCount = nonconformities.filter(nc => nc.classification === "minor").length;
                
                return (
                  <>
                    {/* Summary */}
                    <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">
                          {majorCount} Majör
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm font-medium">
                          {minorCount} Minör
                        </span>
                      </div>
                      <div className="ml-auto">
                        <Button
                          onClick={handleDownloadAllNonconformityReports}
                          className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                          size="sm"
                          data-testid="download-all-nc"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Tüm Raporları İndir ({nonconformities.length})
                        </Button>
                      </div>
                    </div>
                    
                    {/* Nonconformity List */}
                    <div className="space-y-3">
                      {nonconformities.map((nc, index) => (
                        <div 
                          key={nc.criterion_id}
                          className={`p-4 rounded-lg border ${
                            nc.classification === "major" 
                              ? "border-red-200 bg-red-50" 
                              : "border-yellow-200 bg-yellow-50"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-bold text-lg">#{nc.nc_number}</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  nc.classification === "major"
                                    ? "bg-red-200 text-red-800"
                                    : "bg-yellow-200 text-yellow-800"
                                }`}>
                                  {nc.classification === "major" ? "MAJÖR" : "MİNÖR"}
                                </span>
                                <span className="text-sm font-medium text-gray-700">
                                  {nc.criterion_code}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-1">
                                <span className="font-medium">Bölüm:</span> {nc.section_name}
                                {nc.section_name_en && nc.section_name_en !== nc.section_name && (
                                  <span className="text-blue-600 ml-2">/ {nc.section_name_en}</span>
                                )}
                              </p>
                              <div className="text-sm text-gray-700 mb-2">
                                <span className="font-medium">Kriter:</span> 
                                <p className="mt-1">{nc.criterion_text_tr?.substring(0, 150)}{nc.criterion_text_tr?.length > 150 ? "..." : ""}</p>
                                {nc.criterion_text_en && nc.criterion_text_en !== nc.criterion_text_tr && (
                                  <p className="text-blue-600 text-xs mt-1 italic">{nc.criterion_text_en?.substring(0, 150)}{nc.criterion_text_en?.length > 150 ? "..." : ""}</p>
                                )}
                              </div>
                              {(nc.details_tr || nc.details_en) && (
                                <div className="text-sm text-gray-600 border-t pt-2 mt-2">
                                  <span className="font-medium">Açıklama:</span>
                                  {nc.details_tr && <p className="mt-1">{nc.details_tr}</p>}
                                  {nc.details_en && <p className="text-blue-600 text-xs mt-1 italic">[EN] {nc.details_en}</p>}
                                </div>
                              )}
                            </div>
                            <Button
                              onClick={() => handleDownloadNonconformityReport(nc)}
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              data-testid={`download-nc-${nc.nc_number}`}
                            >
                              <FileDown className="w-4 h-4 mr-1" />
                              Rapor İndir
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Floating save button for mobile */}
      {isEditable && (
        <div className="fixed bottom-6 right-6 flex gap-2 md:hidden">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="shadow-lg bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 rounded-full w-14 h-14"
          >
            <Save className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Rapor Geçmişi
            </DialogTitle>
            <DialogDescription>
              {report?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Current Status */}
            <div className="p-3 rounded-lg bg-gray-50 border">
              <p className="text-sm font-medium text-gray-700">Mevcut Durum</p>
              <p className={`text-lg font-bold ${
                report?.status === 'approved' ? 'text-green-600' :
                report?.status === 'rejected' ? 'text-red-600' :
                report?.status === 'submitted' ? 'text-blue-600' :
                'text-gray-600'
              }`}>
                {report?.status === 'draft' && 'Taslak'}
                {report?.status === 'submitted' && 'Onay Bekliyor'}
                {report?.status === 'approved' && 'Onaylandı'}
                {report?.status === 'rejected' && 'Reddedildi'}
                {report?.status === 'under_review' && 'İnceleniyor'}
              </p>
            </div>

            {/* Review Info */}
            {(report?.status === 'approved' || report?.status === 'rejected') && (
              <div className={`p-4 rounded-lg border ${
                report?.status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {report?.status === 'approved' ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`font-medium ${report?.status === 'approved' ? 'text-green-800' : 'text-red-800'}`}>
                      {report?.status === 'approved' ? 'Rapor Onaylandı' : 'Rapor Reddedildi'}
                    </p>
                    {report?.reviewed_by_name && (
                      <p className="text-sm text-gray-600 mt-1">
                        İşlemi Yapan: {report.reviewed_by_name}
                      </p>
                    )}
                    {report?.reviewed_at && (
                      <p className="text-sm text-gray-500">
                        Tarih: {new Date(report.reviewed_at).toLocaleString('tr-TR')}
                      </p>
                    )}
                    {report?.review_notes && (
                      <div className="mt-2 p-2 bg-white rounded border">
                        <p className="text-sm font-medium text-gray-700">Not:</p>
                        <p className="text-sm text-gray-600">{report.review_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Creation Info */}
            <div className="p-3 rounded-lg bg-gray-50 border">
              <p className="text-sm font-medium text-gray-700">Oluşturulma Bilgisi</p>
              {report?.created_by_name && (
                <p className="text-sm text-gray-600">Oluşturan: {report.created_by_name}</p>
              )}
              {report?.created_at && (
                <p className="text-sm text-gray-500">
                  Tarih: {new Date(report.created_at).toLocaleString('tr-TR')}
                </p>
              )}
            </div>

            {/* Last Update Info */}
            {report?.updated_at && (
              <div className="p-3 rounded-lg bg-gray-50 border">
                <p className="text-sm font-medium text-gray-700">Son Güncelleme</p>
                <p className="text-sm text-gray-500">
                  {new Date(report.updated_at).toLocaleString('tr-TR')}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={() => setHistoryDialogOpen(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Validation Errors Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="max-w-md" data-testid="validation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Rapor Eksik
            </DialogTitle>
            <DialogDescription>
              Raporu onaya göndermeden önce aşağıdaki eksiklikleri tamamlayın:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {validationErrors.map((error, index) => (
              <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  {error.type === "interview" && <Users className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                  {error.type === "participants" && <UserCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                  {error.type === "criteria" && <ClipboardList className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <p className="font-medium text-red-800">{error.message}</p>
                    <p className="text-sm text-red-600 mt-1">{error.description}</p>
                    {error.details && (
                      <pre className="text-xs text-red-500 mt-2 whitespace-pre-wrap font-mono bg-red-100/50 p-2 rounded">
                        {error.details}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowValidationDialog(false)} variant="outline">
              Anladım
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <XCircle className="w-8 h-8" />
            </button>
            <img 
              src={selectedPhoto.src}
              alt={selectedPhoto.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-2 text-sm">{selectedPhoto.name}</p>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default InteractiveReportForm;
